import {
  Alert,
  AlertDescription,
  AlertIcon,
  Link as ChakraLink,
  ListItem,
  OrderedList,
  Text,
} from "@chakra-ui/react";
import { useReactFlow } from "@xyflow/react";
import { FormValues } from "features/entities/EntityCreatePage/EntityCreatePage";
import { getEdgeId } from "features/entities/EntityCreatePage/EntityGraph/edges/edgeUtils";
import { DRAFT_NODE_ID } from "features/entities/EntityCreatePage/EntityGraph/entityGraphConstants";
import { InvestingEntities } from "features/entities/EntityCreatePage/FormSidebar/InvestingEntities";
import reject from "lodash-es/reject";
import startCase from "lodash-es/startCase";
import { useCallback } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  ContentBlock,
  ContentBlockBody,
  ContentBlockHeader,
  ContentBlockTitle,
} from "shared/components/content/ContentBlock";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { InfoCircleIcon } from "shared/icons/InfoCircleIcon";
import { rootRoute } from "shared/navigation/Routes";

export const InvestingEdges = () => {
  const { getEdges, addEdges, deleteElements } = useReactFlow();
  const form = useFormContext<FormValues>();
  const { fundFamilyId } = useRequiredStringParams();
  const { t } = useTranslation(["entity", "common"]);

  const investsIntoOnChange = useCallback(
    (newValues: string[]) => {
      const draftOutgoingEdges = getEdges().filter(
        (edge) => edge.source === DRAFT_NODE_ID,
      );
      //   find values without a matching edge
      const missingEdges = reject(newValues, (entityId) => {
        const matchingEdge = draftOutgoingEdges.find(
          (edge) => edge.target === entityId && edge.source === DRAFT_NODE_ID,
        );
        return !!matchingEdge;
      });
      const newEdges = missingEdges.map((edge) => ({
        id: getEdgeId(DRAFT_NODE_ID, edge),
        source: DRAFT_NODE_ID,
        target: edge,
        animated: true,
      }));
      addEdges(newEdges);
      const edgesThatNoLongerExist = draftOutgoingEdges.filter(
        (edge) => !newValues.includes(edge.target),
      );
      void deleteElements({
        edges: edgesThatNoLongerExist,
      });
    },
    [addEdges, deleteElements, getEdges],
  );

  const [legal_name, functional_type] = useWatch({
    control: form.control,
    name: ["entity_data.legal_name", "entity_data.functional_type"],
  });

  const entityName =
    legal_name ||
    functional_type ||
    t("entity:create.investing_edges_input.entity_placeholder");

  return (
    <ContentBlock>
      <ContentBlockHeader>
        <ContentBlockTitle>
          {t("entity:create.investing_edges_input.title", {
            entityName: startCase(entityName),
          })}
        </ContentBlockTitle>
      </ContentBlockHeader>
      <ContentBlockBody px={4} pb={4}>
        <InvestingEntities
          label="Invests into"
          control={form.control}
          emptyText="This entity does not invest into any other entities"
          onChangeValue={investsIntoOnChange}
        />
        <Alert status="info" mb={4} data-ls-enabled={true} data-public={true}>
          <AlertIcon>
            <InfoCircleIcon />
          </AlertIcon>
          <AlertDescription>
            <Text>
              {t("entity:create.investing_edges_input.alert.line1", {
                entityName: startCase(entityName),
              })}
            </Text>
            <OrderedList>
              <ListItem>
                {t("entity:create.investing_edges_input.alert.line2", {
                  entityName: startCase(entityName),
                })}
              </ListItem>
              <ListItem>
                {t("entity:create.investing_edges_input.alert.line3", {
                  entityName: startCase(entityName),
                })}
                <ClosingWorkflowLink fundFamilyId={fundFamilyId} />
              </ListItem>
            </OrderedList>
          </AlertDescription>
        </Alert>
      </ContentBlockBody>
    </ContentBlock>
  );
};

const ClosingWorkflowLink = ({ fundFamilyId }: { fundFamilyId: string }) => (
  <ChakraLink
    to={
      rootRoute({})
        .fundFamilies({})
        .fundFamily({ fundFamilyId })
        .commitments({}).$
    }
    as={Link}
    variant="link"
    pl="4px !important"
  >
    Closing Workflow
  </ChakraLink>
);
